import { useMemo } from 'react';

/**
 * SVG sparkline component for metric cards.
 * Draws a smooth line chart with gradient fill.
 */
export default function Sparkline({
  data = [],
  width = 120,
  height = 40,
  color = '#1E4E8C',
  className = '',
}) {
  const path = useMemo(() => {
    if (!data.length) return '';
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const step = width / Math.max(data.length - 1, 1);

    const points = data.map((val, i) => ({
      x: i * step,
      y: height - ((val - min) / range) * (height - 4) - 2,
    }));

    let d = `M${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const cx = (points[i - 1].x + points[i].x) / 2;
      d += ` C${cx},${points[i - 1].y} ${cx},${points[i].y} ${points[i].x},${points[i].y}`;
    }
    return d;
  }, [data, width, height]);

  const fillPath = useMemo(() => {
    if (!path) return '';
    return `${path} L${width},${height} L0,${height} Z`;
  }, [path, width, height]);

  const gradientId = useMemo(() => `spark-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fillPath && (
        <path d={fillPath} fill={`url(#${gradientId})`} />
      )}
      {path && (
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            strokeDasharray: 1000,
            strokeDashoffset: 1000,
            animation: 'sparkline-draw 800ms ease-out forwards',
          }}
        />
      )}
      <style>{`
        @keyframes sparkline-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </svg>
  );
}
